import { fg } from '@atlaskit/platform-feature-flags';

import { DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI } from '../../../types/default-cloud-graph-calendar-event-entity-ati';
import { DEFAULT_CLOUD_GRAPH_CAMPAIGN_ENTITY_ATI } from '../../../types/default-cloud-graph-campaign-entity-ati';
import { DEFAULT_CLOUD_GRAPH_CASE_ENTITY_ATI } from '../../../types/default-cloud-graph-case-entity-ati';
import { DEFAULT_CLOUD_GRAPH_CUSTOMER_CONTACT_ENTITY_ATI } from '../../../types/default-cloud-graph-customer-contact-entity-ati';
import { DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI } from '../../../types/default-cloud-graph-customer-org-entity-ati';
import { DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI } from '../../../types/default-cloud-graph-deal-entity-ati';
import { DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI } from '../../../types/default-cloud-graph-document-entity-ati';
import { DEFAULT_CLOUD_GRAPH_LEAD_ENTITY_ATI } from '../../../types/default-cloud-graph-lead-entity-ati';
import { DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI } from '../../../types/default-cloud-graph-remote-link-entity-ati';
import { DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI } from '../../../types/default-cloud-graph-work-item-entity-ati';
import { type FilterId } from '../../../types/filters';
import { type ThirdPartyConfig } from '../../../types/schemas/types';
import {
	NounSubtypeKey,
	type TypeFilterValue3PEntities,
	TypeFilterValueKey,
} from '../../../utils/filters/universalType';
import { get3PLogo } from '../../icons';
import { ProductResourceTypes3P } from '../types';

import { SalesforceIcon, SalesforceIconBorderless, SalesforceSearchResultIcon } from './icon';

const getSalesforce3PTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();
	// Entries are ordered alphabetically by display label.
	config.set(TypeFilterValueKey.Campaign, {
		entities3P: [...DEFAULT_CLOUD_GRAPH_CAMPAIGN_ENTITY_ATI],
		subTypes3P: [],
	});
	if (fg('rovo-fps-3p-ux-salesforce-case-noun')) {
		config.set(TypeFilterValueKey.Case, {
			entities3P: [...DEFAULT_CLOUD_GRAPH_CASE_ENTITY_ATI],
			subTypes3P: [],
		});
	}
	if (fg('rovo-fps-3p-ux-salesforce-calendar-noun')) {
		config.set(TypeFilterValueKey.Calendar, {
			entities3P: [...DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI],
			subTypes3P: [],
		});
	}
	config.set(TypeFilterValueKey.CustomerContact, {
		entities3P: [...DEFAULT_CLOUD_GRAPH_CUSTOMER_CONTACT_ENTITY_ATI],
		subTypes3P: [],
	});
	config.set(TypeFilterValueKey.CustomerOrg, {
		entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
		subTypes3P: [],
	});
	config.set(TypeFilterValueKey.Deal, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Deal],
	});
	if (fg('rovo-fps-3p-ux-salesforce-document-noun')) {
		config.set(TypeFilterValueKey.Document, {
			entities3P: [...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI],
			subTypes3P: [],
		});
	}
	if (fg('rovo-fps-3p-ux-salesforce-lead-noun')) {
		config.set(TypeFilterValueKey.Lead, {
			entities3P: [...DEFAULT_CLOUD_GRAPH_LEAD_ENTITY_ATI],
			subTypes3P: [],
		});
	}
	if (fg('rovo-fps-3p-ux-salesforce-work-item-noun')) {
		config.set(TypeFilterValueKey.WorkItem, {
			entities3P: [...DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI],
			subTypes3P: [],
		});
	}
	return config;
};

export const salesforceConfig: ThirdPartyConfig = {
	displayName: 'Salesforce',
	id: 'salesforce',
	resourceType: ProductResourceTypes3P.Salesforce,
	icon: get3PLogo(SalesforceIcon, 'Salesforce', 'medium'),
	iconBorderless: get3PLogo(SalesforceIconBorderless, 'Salesforce', 'small'),
	searchResultIcon: get3PLogo(SalesforceSearchResultIcon, 'Salesforce', 'large'),
	integrationAri: 'ari:cloud:platform::integration/salesforce',
	// Use getters to evaluate lazily so that feature flags are evaluated when accessed, not at module load.
	get typeFilterConfig() {
		return getSalesforce3PTypeFilterConfig();
	},
	get filters(): FilterId[] {
		const base: FilterId[] = [
			'lastUpdated',
			'onlyTitleMatch',
			'lastUpdatedBy',
			'createdBy',
			'owner',
			'opportunityStatus',
			'closeDate',
		];
		if (
			fg('rovo-fps-3p-ux-salesforce-dynamic-stage-filter') ||
			fg('rovo-studio-3p-ux-salesforce-dynamic-stage-filter')
		) {
			base.push('salesforceStage');
		}
		base.push('campaignStatus');
		base.push('caseStatus');
		if (fg('rovo-fps-3p-ux-salesforce-document-status-filter')) {
			base.push('documentStatus');
		}
		if (fg('rovo-fps-3p-ux-salesforce-calendar-type-filter')) {
			base.push('calendarType');
		}
		if (fg('rovo-fps-3p-ux-sf-lead-status-filter')) {
			base.push('leadStatus');
		}
		if (fg('rovo-fps-3p-ux-salesforce-task-status-filter')) {
			base.push('taskStatus');
		}
		return base;
	},
	requiresOauth: true,
	isEnabled: () => true,
};
